'use client';

import { useState } from 'react';
import Link from 'next/link';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

interface CaseStudy {
  id: string;
  slug: string;
  company: string;
  industry: string;
  industryBadgeColor: { bg: string; text: string };
  size: string;
  aiSystems: number;
  challenge: string;
  solution: string;
  cmriTimeline: { month: string; score: number }[];
  cmriStart: number;
  cmriEnd: number;
  cmriChange: number;
  results: { label: string; value: string; sub?: string }[];
  quote: string;
  quoteName: string;
  quoteTitle: string;
  certifications: string[];
  keyMetric: string;
  keyMetricLabel: string;
}

const caseStudies: CaseStudy[] = [
  {
    id: 'financial-services',
    slug: 'financial-services',
    company: 'Global Investment Bank',
    industry: 'Financial Services',
    industryBadgeColor: { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa' },
    size: '12,000 employees',
    aiSystems: 47,
    challenge: 'Managing 47 AI models across trading, credit, and fraud detection with no centralized governance. Failed internal audit Q3 2024. ISO 42001 certification target by end of year.',
    solution: 'Deployed ChronoMind™ Enterprise, mapped all 47 use cases in 2 weeks, implemented automated risk assessments, and connected compliance workflows across all business units.',
    cmriTimeline: [
      { month: 'Month 0', score: 34 },
      { month: 'Month 3', score: 52 },
      { month: 'Month 6', score: 71 },
      { month: 'Month 9', score: 84 },
    ],
    cmriStart: 34,
    cmriEnd: 84,
    cmriChange: 147,
    results: [
      { label: 'CMRI Improvement', value: '34 → 84', sub: '+147%' },
      { label: 'ISO 42001 Readiness', value: '6 months', sub: 'Industry avg: 18 months' },
      { label: 'Compliance Hours Saved', value: '340 hrs', sub: 'Per month' },
      { label: 'Annual Cost Savings', value: '$2.1M', sub: 'Verified savings' },
      { label: 'Audit Findings Reduced', value: '89%', sub: 'Reduction' },
    ],
    quote: 'ChronoMind gave us a single source of truth for every AI system. Our auditors were impressed.',
    quoteName: 'Chief Compliance Officer',
    quoteTitle: 'Global Investment Bank',
    certifications: ['ISO 42001', 'EU AI Act Compliance'],
    keyMetric: '+147%',
    keyMetricLabel: 'CMRI Improvement',
  },
  {
    id: 'healthcare',
    slug: 'healthcare',
    company: 'Regional Health Network',
    industry: 'Healthcare',
    industryBadgeColor: { bg: 'rgba(34,197,94,0.15)', text: '#4ade80' },
    size: '8,500 employees',
    aiSystems: 23,
    challenge: '23 AI systems including diagnostic imaging, patient triage, and administrative AI. HIPAA + EU AI Act high-risk classification concerns. No documented risk assessments.',
    solution: 'ChronoMind™ Professional tier with automated DPIA workflows for all diagnostic AI, EU AI Act conformity assessments, and controls library mapped to HIPAA requirements.',
    cmriTimeline: [
      { month: 'Month 0', score: 28 },
      { month: 'Month 2', score: 45 },
      { month: 'Month 4', score: 63 },
      { month: 'Month 8', score: 79 },
    ],
    cmriStart: 28,
    cmriEnd: 79,
    cmriChange: 182,
    results: [
      { label: 'CMRI Improvement', value: '28 → 79', sub: '+182%' },
      { label: 'DPIAs Completed', value: '23', sub: 'In 3 weeks (previously: 0)' },
      { label: 'Regulatory Findings', value: 'Zero', sub: 'Last 2 audits' },
      { label: 'Staff Training', value: '94%', sub: 'Completion rate' },
      { label: 'Compliance Cost Reduction', value: '$890K', sub: 'Annual savings' },
    ],
    quote: 'We went from zero documentation to full EU AI Act compliance in under 6 months.',
    quoteName: 'VP of Digital Health',
    quoteTitle: 'Regional Health Network',
    certifications: ['GDPR Compliant', 'EU AI Act High-Risk'],
    keyMetric: '+182%',
    keyMetricLabel: 'CMRI Improvement',
  },
  {
    id: 'government',
    slug: 'government',
    company: 'Federal Civilian Agency',
    industry: 'Government',
    industryBadgeColor: { bg: 'rgba(168,85,247,0.15)', text: '#c084fc' },
    size: '3,200 employees',
    aiSystems: 15,
    challenge: 'Executive Order on AI compliance requirements, OMB AI policy deadlines, NIST AI RMF alignment needed. Limited internal compliance resources and tight federal timelines.',
    solution: 'ChronoMind™ Enterprise with NIST AI RMF framework, custom federal controls library, and automated board reporting for agency leadership and Inspector General.',
    cmriTimeline: [
      { month: 'Month 0', score: 41 },
      { month: 'Month 2', score: 58 },
      { month: 'Month 5', score: 74 },
      { month: 'Month 7', score: 88 },
    ],
    cmriStart: 41,
    cmriEnd: 88,
    cmriChange: 115,
    results: [
      { label: 'CMRI Improvement', value: '41 → 88', sub: '+115%' },
      { label: 'OMB Deadline', value: '3 weeks early', sub: 'Ahead of schedule' },
      { label: 'AI Inventory', value: '100%', sub: '15/15 systems documented' },
      { label: 'IG Audit Rating', value: 'Satisfactory', sub: 'Inspector General' },
      { label: 'Annual Efficiency Savings', value: '$1.4M', sub: 'Verified savings' },
    ],
    quote: 'ChronoMind helped us meet the Presidential AI Executive Order requirements ahead of schedule.',
    quoteName: 'Agency CIO',
    quoteTitle: 'Federal Civilian Agency',
    certifications: ['NIST AI RMF', 'FedRAMP-Ready Controls'],
    keyMetric: '+115%',
    keyMetricLabel: 'CMRI Improvement',
  },
  {
    id: 'insurance',
    slug: 'insurance',
    company: 'National Insurance Group',
    industry: 'Insurance',
    industryBadgeColor: { bg: 'rgba(251,191,36,0.15)', text: '#fbbf24' },
    size: '6,100 employees',
    aiSystems: 31,
    challenge: 'Predictive underwriting and claims AI flagged by state regulators for algorithmic bias concerns. Needed audit trail and explainability documentation urgently.',
    solution: 'ChronoMind™ Enterprise with bias detection controls, automated audit trails, and escalation workflows for regulator inquiries and state examination responses.',
    cmriTimeline: [
      { month: 'Month 0', score: 38 },
      { month: 'Month 1', score: 54 },
      { month: 'Month 4', score: 69 },
      { month: 'Month 6', score: 82 },
    ],
    cmriStart: 38,
    cmriEnd: 82,
    cmriChange: 116,
    results: [
      { label: 'CMRI Improvement', value: '38 → 82', sub: '+116%' },
      { label: 'Regulator Response Time', value: '4 hours', sub: 'Previously: 3 weeks' },
      { label: 'Bias Assessment Coverage', value: '100%', sub: 'All decision-making AI' },
      { label: 'Regulatory Inquiries', value: '4/4', sub: 'Resolved with no penalties' },
      { label: 'Risk Exposure Reduction', value: '$3.2M', sub: 'Annual reduction' },
    ],
    quote: 'When regulators called, we had every answer documented and ready in ChronoMind.',
    quoteName: 'Chief Risk Officer',
    quoteTitle: 'National Insurance Group',
    certifications: ['ISO 42001', 'SOC 2 Type I'],
    keyMetric: '+116%',
    keyMetricLabel: 'CMRI Improvement',
  },
];

const industries = ['All', 'Financial Services', 'Healthcare', 'Government', 'Insurance'];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border px-3 py-2" style={{ backgroundColor: '#0F172A', borderColor: 'rgba(201,169,97,0.4)' }}>
        <p className="font-body text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{label}</p>
        <p className="font-heading text-base font-bold" style={{ color: '#C9A961' }}>CMRI: {payload[0].value}</p>
      </div>
    );
  }
  return null;
};

function CMRIChart({ data }: { data: { month: string; score: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height={160}>
      <LineChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
        <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
        <YAxis domain={[0, 100]} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
        <Tooltip content={<CustomTooltip />} />
        <ReferenceLine y={70} stroke="rgba(201,169,97,0.3)" strokeDasharray="4 4" />
        <Line
          type="monotone"
          dataKey="score"
          stroke="#C9A961"
          strokeWidth={2.5}
          dot={{ fill: '#C9A961', r: 4, strokeWidth: 0 }}
          activeDot={{ r: 6, fill: '#C9A961', stroke: 'rgba(201,169,97,0.4)', strokeWidth: 3 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function CaseStudiesPage() {
  const [activeIndustry, setActiveIndustry] = useState('All');
  const [expandedStudy, setExpandedStudy] = useState<string | null>(null);

  const filtered = activeIndustry === 'All' ? caseStudies : caseStudies.filter((cs) => cs.industry === activeIndustry);

  const handleShare = (type: string, cs: CaseStudy) => {
    if (type === 'linkedin') {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href + '#' + cs.slug)}`, '_blank');
    } else if (type === 'email') {
      window.location.href = `mailto:?subject=ChronoMind™ Case Study: ${cs.industry}&body=Check out this case study: ${window.location.href}#${cs.slug}`;
    } else if (type === 'copy') {
      navigator.clipboard.writeText(window.location.href + '#' + cs.slug);
      alert('Link copied!');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0F172A', color: '#ffffff' }}>
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-md" style={{ backgroundColor: 'rgba(15,23,42,0.95)', borderColor: 'rgba(201,169,97,0.2)' }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link href="/" className="flex items-center gap-2">
              <span className="font-heading text-lg font-bold" style={{ color: '#C9A961' }}>ChronoMind™</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link href="/templates-library" className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>Templates</Link>
              <Link href="/compliance-cost-roi-report" className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>ROI Report</Link>
              <Link href="/login?mode=signup" className="rounded-md px-4 py-2 font-body text-sm font-semibold" style={{ backgroundColor: '#C9A961', color: '#0F172A' }}>Start Free Trial</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-16">
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 text-center" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #0F172A 100%)' }}>
          <div className="mx-auto max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-6 border" style={{ backgroundColor: 'rgba(201,169,97,0.1)', borderColor: 'rgba(201,169,97,0.3)' }}>
              <span className="font-body text-xs font-semibold tracking-widest uppercase" style={{ color: '#C9A961' }}>Customer Success</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ letterSpacing: '-0.025em', lineHeight: '1.1' }}>
              Customer Success Stories
            </h1>
            <p className="font-body text-lg lg:text-xl mb-6" style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '1.5rem' }}>
              See how leading organizations transformed their AI governance with ChronoMind™
            </p>
            {/* Disclaimer */}
            <div className="inline-flex items-start gap-2 rounded-lg border px-4 py-3 text-left" style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.1)' }}>
              <svg className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-body text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                The following case studies represent composite examples based on common implementation patterns. Individual results may vary.
              </p>
            </div>
          </div>
        </section>

        {/* Industry Filter */}
        <section className="sticky top-16 z-40 border-b px-4 sm:px-6 lg:px-8 py-4" style={{ backgroundColor: 'rgba(15,23,42,0.97)', borderColor: 'rgba(201,169,97,0.15)', backdropFilter: 'blur(12px)' }}>
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-wrap gap-2">
              {industries.map((ind) => (
                <button
                  key={ind}
                  onClick={() => setActiveIndustry(ind)}
                  className="rounded-full px-4 py-1.5 font-body text-xs font-semibold transition-all"
                  style={{
                    backgroundColor: activeIndustry === ind ? '#C9A961' : 'rgba(255,255,255,0.06)',
                    color: activeIndustry === ind ? '#0F172A' : 'rgba(255,255,255,0.65)',
                    border: activeIndustry === ind ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  {ind}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Case Study Cards Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {filtered.map((cs) => (
                <div
                  key={cs.id}
                  id={cs.slug}
                  className="rounded-2xl border overflow-hidden transition-all duration-300 hover:border-[#C9A961]/40"
                  style={{ background: 'linear-gradient(135deg, rgba(30,58,95,0.4) 0%, rgba(15,23,42,0.9) 100%)', borderColor: 'rgba(255,255,255,0.1)' }}
                >
                  {/* Card Header */}
                  <div className="p-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <span
                          className="inline-block rounded-full px-3 py-1 font-body text-xs font-semibold mb-2"
                          style={{ backgroundColor: cs.industryBadgeColor.bg, color: cs.industryBadgeColor.text }}
                        >
                          {cs.industry}
                        </span>
                        <h2 className="font-heading text-xl font-bold text-white">{cs.company}</h2>
                        <p className="font-body text-sm mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
                          {cs.size} · {cs.aiSystems} AI Systems
                        </p>
                      </div>
                      {/* Key metric highlight */}
                      <div className="text-right flex-shrink-0">
                        <div className="font-heading text-3xl font-bold" style={{ color: '#C9A961' }}>{cs.keyMetric}</div>
                        <div className="font-body text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{cs.keyMetricLabel}</div>
                      </div>
                    </div>

                    {/* CMRI Timeline Chart */}
                    <div className="mb-2">
                      <p className="font-body text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'rgba(201,169,97,0.7)' }}>CMRI Governance Score Timeline</p>
                      <CMRIChart data={cs.cmriTimeline} />
                    </div>
                  </div>

                  {/* Results metrics */}
                  <div className="p-6">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                      {cs.results.slice(0, 3).map((result) => (
                        <div key={result.label} className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                          <div className="font-heading text-lg font-bold" style={{ color: '#C9A961' }}>{result.value}</div>
                          <div className="font-body text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>{result.label}</div>
                          {result.sub && <div className="font-body text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{result.sub}</div>}
                        </div>
                      ))}
                    </div>

                    {/* Expandable detail */}
                    {expandedStudy === cs.id && (
                      <div className="mb-5">
                        {/* All results */}
                        <div className="grid grid-cols-2 gap-3 mb-5">
                          {cs.results.slice(3).map((result) => (
                            <div key={result.label} className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                              <div className="font-heading text-lg font-bold" style={{ color: '#C9A961' }}>{result.value}</div>
                              <div className="font-body text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>{result.label}</div>
                              {result.sub && <div className="font-body text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{result.sub}</div>}
                            </div>
                          ))}
                        </div>

                        {/* Challenge */}
                        <div className="mb-4">
                          <h4 className="font-heading text-sm font-bold text-white mb-2">Challenge</h4>
                          <p className="font-body text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>{cs.challenge}</p>
                        </div>

                        {/* Solution */}
                        <div className="mb-4">
                          <h4 className="font-heading text-sm font-bold text-white mb-2">Solution</h4>
                          <p className="font-body text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>{cs.solution}</p>
                        </div>

                        {/* Pull quote */}
                        <blockquote className="rounded-xl border-l-4 p-5 mb-4" style={{ borderColor: '#C9A961', backgroundColor: 'rgba(201,169,97,0.06)' }}>
                          <p className="font-body text-sm italic leading-relaxed mb-3" style={{ color: 'rgba(255,255,255,0.8)' }}>
                            &ldquo;{cs.quote}&rdquo;
                          </p>
                          <footer className="font-body text-xs font-semibold" style={{ color: '#C9A961' }}>
                            — {cs.quoteName}, {cs.quoteTitle}
                          </footer>
                        </blockquote>

                        {/* Certifications */}
                        <div className="mb-4">
                          <h4 className="font-heading text-sm font-bold text-white mb-2">Certifications Achieved</h4>
                          <div className="flex flex-wrap gap-2">
                            {cs.certifications.map((cert) => (
                              <span key={cert} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-body text-xs font-semibold" style={{ backgroundColor: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.25)' }}>
                                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                {cert}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Share buttons */}
                        <div className="flex items-center gap-2">
                          <span className="font-body text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Share:</span>
                          <button onClick={() => handleShare('linkedin', cs)} className="rounded-md px-3 py-1.5 font-body text-xs font-medium transition-colors hover:bg-white/10" style={{ border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}>LinkedIn</button>
                          <button onClick={() => handleShare('email', cs)} className="rounded-md px-3 py-1.5 font-body text-xs font-medium transition-colors hover:bg-white/10" style={{ border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}>Email</button>
                          <button onClick={() => handleShare('copy', cs)} className="rounded-md px-3 py-1.5 font-body text-xs font-medium transition-colors hover:bg-white/10" style={{ border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}>Copy Link</button>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => setExpandedStudy(expandedStudy === cs.id ? null : cs.id)}
                        className="flex-1 rounded-lg py-2.5 font-body text-sm font-semibold transition-all hover:scale-105"
                        style={{ backgroundColor: '#C9A961', color: '#0F172A' }}
                      >
                        {expandedStudy === cs.id ? 'Show Less' : 'Read Full Story'}
                      </button>
                      <button
                        onClick={() => window.print()}
                        className="rounded-lg px-4 py-2.5 font-body text-sm font-medium transition-colors hover:bg-white/10"
                        style={{ border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}
                        title="Download as PDF"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Aggregate metrics summary */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'linear-gradient(180deg, #0a1628 0%, #0F172A 100%)' }}>
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white" style={{ letterSpacing: '-0.025em', marginBottom: '1.5rem' }}>Results Across All Case Studies</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { value: 'Avg +140%', label: 'CMRI Improvement' },
                { value: '$7.6M+', label: 'Total Cost Savings' },
                { value: '< 6 mo', label: 'Avg Time to Compliance' },
                { value: '100%', label: 'Audit Success Rate' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-xl border p-5 text-center" style={{ backgroundColor: 'rgba(30,58,95,0.3)', borderColor: 'rgba(201,169,97,0.2)' }}>
                  <div className="font-heading text-2xl font-bold mb-1" style={{ color: '#C9A961' }}>{stat.value}</div>
                  <div className="font-body text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(135deg, rgba(30,58,95,0.6) 0%, rgba(15,23,42,0.98) 100%)' }}>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4" style={{ letterSpacing: '-0.025em', marginBottom: '1.5rem' }}>
              Ready to write your success story?
            </h2>
            <p className="font-body text-lg mb-8" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Join organizations that have transformed their AI governance with ChronoMind™.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/login?mode=signup"
                className="rounded-lg py-4 px-10 font-body text-base font-bold transition-all hover:scale-105"
                style={{ backgroundColor: '#C9A961', color: '#0F172A', boxShadow: '0 4px 20px rgba(201,169,97,0.35)' }}
              >
                Start Free Trial
              </Link>
              <Link
                href="/templates-library"
                className="rounded-lg border-2 py-4 px-10 font-body text-base font-semibold transition-all hover:scale-105"
                style={{ borderColor: 'rgba(201,169,97,0.5)', color: '#C9A961' }}
              >
                Download Templates
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}